/**
 * Stage pill component for pipeline stages.
 *
 * Displays a single pipeline stage with its status.
 *
 * Implementation plan reference: D2.15
 */

import type { StageStatus } from '@/types/api';
import { formatDateTime } from '@/utils/format';

interface StagePillProps {
  name: string;
  status: StageStatus;
  durationSecs?: number | null;
  completedAt?: string | null;
  /** When set, the pill links to the GitHub Actions run (or related workflow URL). */
  url?: string | null;
}

const STATUS_LABELS: Record<StageStatus, string> = {
  passed: 'Passed',
  failed: 'Failed',
  running: 'Running',
  blocked: 'Blocked',
  'pending-gate': 'Pending Gate',
  'not-implemented': 'Not Implemented',
};

const formatCompletedAt = (iso: string): string =>
  formatDateTime(iso, { dateStyle: 'short', timeStyle: 'short' });

export function StagePill({ name, status, durationSecs, completedAt, url }: StagePillProps): JSX.Element {
  const duration = durationSecs != null
    ? `${Math.floor(durationSecs / 60)}m ${durationSecs % 60}s`
    : null;

  const completed = completedAt !== undefined && completedAt !== null && completedAt !== ''
    ? formatCompletedAt(completedAt)
    : null;

  const hasUrl = url !== undefined && url !== null && url !== '';

  const pill = (
    <div className={`stage-pill stage-${status}`}>
      <span className="stage-name">{name}</span>
      <span className="stage-status">{STATUS_LABELS[status]}</span>
      {duration !== null && <span className="stage-duration">{duration}</span>}
      {completed !== null && <span className="stage-completed">{completed}</span>}
    </div>
  );

  if (hasUrl) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="stage-pill-link"
        title="View workflow run on GitHub"
      >
        {pill}
      </a>
    );
  }

  return pill;
}
